import { NgOptimizedImage } from '@angular/common'
import type { OnChanges, SimpleChanges } from '@angular/core'
import { Component, inject, input } from '@angular/core'
import { ActivatedRoute, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'

import type { ArticleCategory } from '../../api/responses'
import { TruncateCleanPipe } from '../../shared/pipes/truncate-clean.pipe'
import type {
  ArticleWithNullableRefs,
  AuthorWithContext,
} from '../../types/types'
import { ArticleCategoryList } from './components/article-category-list'
import { ArticleMetaData } from './components/article-meta-data'
import { ArticleTypeBadge } from './components/article-type-badge'
import type { ArticleManual } from './components/manual-dialog'
import { ManualDialog } from './components/manual-dialog'

@Component({
  standalone: true,
  selector: 'dhp-article-list',
  templateUrl: './article-list.html',
  styleUrls: ['./article-list.scss'],
  imports: [
    RouterLink,
    TranslatePipe,
    TruncateCleanPipe,
    ManualDialog,
    NgOptimizedImage,
    ArticleCategoryList,
    ArticleMetaData,
    ArticleTypeBadge,
  ],
})
export class ArticleList implements OnChanges {
  private route = inject(ActivatedRoute)

  readonly articles = input<ArticleWithNullableRefs[]>([])
  // The embedContext changes the layout and positioning of content slightly
  readonly embedContext = input<
    | 'search'
    | 'edition'
    | 'authorDetails'
    | 'selectieAndEssentie'
    | 'magazineEditionOverview'
    | null
  >(null)

  // Current context for page
  private contextType: 'author' | 'editor' | null = null
  public adaptedArticles: ArticleWithNullableRefs[] = []

  protected selectedArticleManual: ArticleManual | null = null

  constructor() {
    // Determine the current context from the route
    this.determineCurrentContext()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['articles']) {
      this.adaptArticles()
    }
  }

  adaptArticles(): void {
    // Process all authors to ensure proper linkType.
    // Important: do not mutate `article` / `article.users` because GraphQL results may be frozen/readonly.
    this.adaptedArticles = this.articles().map(article => {
      const existingUsers = article?.users
      const existingAuthors = existingUsers?.authors ?? []

      const adaptedAuthors = existingAuthors.map(author => {
        const authorWithContext = author as unknown as AuthorWithContext

        const desiredLinkType: 'author' | 'editor' =
          authorWithContext.linkType ??
          authorWithContext.linkContext ??
          author.type ??
          this.contextType ??
          'author'

        const desiredId = (authorWithContext as any).id ?? author.id

        // Return a new object only when we need to change/ensure fields; otherwise preserve reference.
        if (
          authorWithContext.linkType !== desiredLinkType ||
          (authorWithContext as any).id !== desiredId
        ) {
          return {
            ...author,
            linkType: desiredLinkType,
            id: desiredId,
          }
        }

        return author
      })

      return {
        ...article,
        users: {
          // keep other user fields stable, but ensure we create a new object
          ...(existingUsers ?? ({} as any)),
          authors: adaptedAuthors,
          // ensure creators exists for templates that may expect it
          creators: existingUsers?.creators ?? [],
        },
      }
    })
  }

  private determineCurrentContext(): void {
    // Navigate up through parent routes to find the context type
    let currentRoute = this.route
    while (currentRoute) {
      // Check if this route has a 'type' data property
      if (currentRoute.snapshot.data['type']) {
        this.contextType = currentRoute.snapshot.data['type']
        break
      }

      // Move to parent route if available
      if (currentRoute.parent) {
        currentRoute = currentRoute.parent
      } else {
        break
      }
    }

    // Check URL as a fallback
    if (!this.contextType) {
      const url = this.route.snapshot.pathFromRoot
        .map(segment =>
          segment.url.map(urlSegment => urlSegment.path).join('/')
        )
        .join('/')

      if (url.includes('/editors/')) {
        this.contextType = 'editor'
      } else if (url.includes('/authors/')) {
        this.contextType = 'author'
      }
    }
  }

  onArticleCategoryClick(manualCategory: {
    article: ArticleWithNullableRefs
    category: ArticleCategory
  }): void {
    this.selectedArticleManual = {
      title: manualCategory.article.magazine?.manual_title ?? '',
      categoryTitle: manualCategory.category?.title ?? '',
      description: manualCategory.article.magazine?.manual_description ?? '',
      baseUrl: manualCategory.article.magazine?.manual_url ?? '',
      pageNumber: manualCategory.category.manual_page_number ?? 0,
      preface: manualCategory.category.manual_preface ?? '',
    }
  }
}
